// ============================================================================
// CECODEV Platform - API Client
// ============================================================================

import axios from "axios";

// Extend Axios types to include custom config options
declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

/** Base des appels API — UNE définition (lot 3 du refactoring) ; les pages publiques à jeton l'importent. */
export const API_URL = import.meta.env.VITE_API_URL || "/api";

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include cookies
});

// Helper : extrait le token public d'epreuve depuis l'URL (route /epreuve/:token)
// pour attacher le binding navigateur correspondant.
function getEpreuveTokenFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  const match = url.match(/\/epreuve-public\/([a-zA-Z0-9_-]{32,})/);
  return match ? match[1] : null;
}

function getOrCreateBinding(epreuveToken: string): string {
  const key = `epreuve-binding-${epreuveToken}`;
  let binding = localStorage.getItem(key);
  if (!binding) {
    binding = (crypto.randomUUID?.() ?? Math.random().toString(36).repeat(3)).replace(/-/g, "");
    localStorage.setItem(key, binding);
  }
  return binding;
}

// Add token + binding aux requetes
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Binding pour l'epreuve publique : evite qu'un lien email forwarde a un tiers
  // soit utilisable sur l'appareil du tiers.
  const epreuveToken = getEpreuveTokenFromUrl(config.url);
  if (epreuveToken) {
    config.headers["X-Epreuve-Binding"] = getOrCreateBinding(epreuveToken);
  }
  return config;
});

// Handle 401 errors + persist binding renvoye par le serveur a la 1re ouverture
api.interceptors.response.use(
  (response) => {
    const epreuveToken = getEpreuveTokenFromUrl(response.config?.url);
    const newBinding = response.headers?.["x-epreuve-binding"];
    if (epreuveToken && newBinding && typeof newBinding === "string") {
      localStorage.setItem(`epreuve-binding-${epreuveToken}`, newBinding);
    }
    return response;
  },
  (error) => {
    // Skip redirect if request was aborted or explicitly opted out
    const skipRedirect = error.config?.skipAuthRedirect || error.code === "ERR_CANCELED";

    if (error.response?.status === 401 && !skipRedirect) {
      // On ne renvoie vers /login QUE si une session authentifiée vient
      // d'expirer (un token existait). Un visiteur ANONYME (aucun token — ex.
      // catalogue public) ne doit jamais être éjecté par le 401 d'une requête
      // de fond (poll notifications, tracker d'usage…). Le gating des pages
      // protégées est assuré par <ProtectedRoute> (React Router), pas ici.
      const hadToken = !!localStorage.getItem("token");
      localStorage.removeItem("token");
      if (hadToken && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// ─── Vocabulaire d'erreur côté client (lot 3 du refactoring, 2026-10-13) ────
// `catch (err)` est typé `unknown` : plus de `catch (err: any)` ni de
// `err.response?.data?.error || "…"` recopié 300 fois. Un seul endroit sait
// à quoi ressemble une erreur axios / une erreur de l'API.

/** Corps JSON d'erreur renvoyé par l'API ({ error, code, details… }), sinon null. */
type ErreurAxiosLite = { response?: { data?: unknown; status?: number }; code?: string };

export function donneesErreurApi(err: unknown): Record<string, unknown> | null {
  if (!axios.isAxiosError(err)) return null;
  const d: unknown = (err as ErreurAxiosLite).response?.data;
  return d && typeof d === "object" ? (d as Record<string, unknown>) : null;
}

/** Message à afficher : le champ `error` du corps de réponse, sinon le défaut. */
export function messageApi(err: unknown, defaut = "Erreur"): string {
  const e = donneesErreurApi(err)?.error;
  return typeof e === "string" && e ? e : defaut;
}

/** Statut HTTP de la réponse en erreur, sinon null (réseau, annulation, erreur JS). */
export function statutApi(err: unknown): number | null {
  return axios.isAxiosError(err) ? ((err as ErreurAxiosLite).response?.status ?? null) : null;
}

/** Champ texte libre du corps d'erreur (`message`, `details`, `erreur`…), sinon null. */
export function champApi(err: unknown, cle: string): string | null {
  const v = donneesErreurApi(err)?.[cle];
  return typeof v === "string" && v ? v : null;
}

/** Code métier porté par le corps d'erreur (`code`), sinon null. */
export function codeApi(err: unknown): string | null {
  const c = donneesErreurApi(err)?.code;
  return typeof c === "string" ? c : null;
}

/** Message d'une erreur JS (hors réponse API) : Error → message, sinon la valeur en chaîne. */
export function messageErreur(err: unknown): string {
  if (err instanceof Error) return err.message;
  return typeof err === "string" ? err : String(err);
}

/** Requête annulée (AbortController, axios ERR_CANCELED) : à ignorer sans toast. */
export function estAnnulation(err: unknown): boolean {
  if (axios.isAxiosError(err) && (err as ErreurAxiosLite).code === "ERR_CANCELED") return true;
  return err instanceof Error && (err.name === "AbortError" || err.name === "CanceledError");
}
